<script lang="ts">
	import type { MultipleLineChartOption } from "@hitit/new_ui/src";
	import { MultipleLineChart } from "@hitit/new_ui/src";
	import { ChartIcon } from "@hitit/new_ui/src";
	import { formatISO } from "date-fns";
	import {
		getAllDateInRange,
		getChartDateKey,
		getResolutionOfRange,
		xAxisLabel,
		chartToolTipDateFormat,
		formatKrwNumber,
	} from "@hitit/hub/src/v7/utils";
	import type { Resolution } from "@hitit/hub/src/v7/utils";
	import { api, t } from "@hitit/hub/src/stores";
	import { onDestroy } from "svelte";

	export let storeIds: string[] = [];
	export let range = {
		start: new Date(),
		end: new Date(),
	};
	type ChartData = {
		sum: number;
		date: Date;
		class: string;
	};

	const areaGradient = [
		{
			dataClass: "salesIncome",
			stopDatas: [
				{
					offset: "0%",
					style: "stop-color:#6591fa;stop-opacity:0.1",
				},
				{
					offset: "90%",
					style: "stop-color:white;stop-opacity:0.1",
				},
			],
		},
	];

	let loading = false;
	// Tooltip
	let parseDate = "";
	let sum = "";
	// El
	let svg: SVGSVGElement;
	let tooltip: HTMLDivElement;
	let chart: HTMLDivElement;
	//For Chart
	let datas: ChartData[] = [];
	let allDates: Date[] = [];
	let __resizeEvent__ = () => {};
	let resolution: Resolution = "HOURLY";

	function getRevenue(
		range: { start: Date; end: Date },
		storeId: string,
		resolution: string
	) {
		const { start, end } = range;
		return $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: resolution,
			storeid: storeId,
		});
	}
	const mousemoveEvent = (event: MouseEvent, datas: ChartData[]) => {
		const { clientX, clientY } = event;

		const [firstData] = datas;
		parseDate = chartToolTipDateFormat(firstData.date, resolution);
		sum = formatKrwNumber(firstData.sum);

		tooltip.style.display = "block";

		const { innerWidth } = window;
		const { width } = tooltip.getBoundingClientRect();
		tooltip.style.top = clientY + 20 + "px";
		if (innerWidth > clientX + 20 + width) {
			tooltip.style.left = clientX + 20 + "px";
		} else {
			let left = clientX - (20 + width);
			if (left < 0) {
				left = left * -1;
			}

			tooltip.style.left = left + "px";
		}
	};
	const mouseoutEvent = (e: MouseEvent) => {
		tooltip.style.display = "none";
	};

	//Do
	$: (async (__storeIds: string[], __range: { start: Date; end: Date }) => {
		try {
			loading = true;
			resolution = getResolutionOfRange(range);
			allDates = getAllDateInRange(range, resolution);
			const map = new Map();
			const originStoreIds = storeIds;
			const originRange = range;

			const allRevenue = __storeIds.map((id) =>
				getRevenue(__range, id, resolution)
			);
			const promises = await Promise.all(allRevenue);
			//
			if (originStoreIds !== __storeIds || originRange !== __range) {
				console.log("Canceld");
				return;
			}

			for (const result of promises) {
				const salesReports = result.data.salesReports;

				for (const salesData of salesReports) {
					const { year, month, day, hour, sum } = salesData;
					const key = `${year}-${month}-${day}-${hour}`;
					const originValue = map.get(key) || 0;
					map.set(key, originValue + sum);
				}
			}

			const chartData = allDates.map((v) => {
				return {
					date: v,
					sum: map.get(getChartDateKey(v, resolution)) || 0,
					class: "salesIncome",
				};
			});
			datas = chartData;
		} catch (error) {
			console.log(error);
		}
	})(storeIds, range);
	//
	$: if (chart && datas) {
		if (svg) {
			svg.remove();
			window.removeEventListener("resize", __resizeEvent__);
		}
		const chartOption: MultipleLineChartOption<ChartData> = {
			classification: "class",
			y: (d: ChartData) => d.sum,
			margin: {
				left: 15,
				right: 15,
				top: 15,
				bottom: 40,
			},
			areaGradient,
			xAxislabel: xAxisLabel(resolution, allDates),
			yDomainCorrection: (yMax) => (yMax > 50 ? yMax : 50),
			mousemoveEvent,
			mouseoutEvent,
		};
		const { node, resizeEvent } = MultipleLineChart(chart, datas, chartOption);
		svg = node as SVGSVGElement;
		__resizeEvent__ = resizeEvent;
	}

	onDestroy(() => {
		window.removeEventListener("resize", __resizeEvent__);
	});

</script>

<div class="container">
	<div class="header">
		<span class="icon">
			<ChartIcon />
		</span>
		<p>{$t('hub.dashboard.graph.Sales_Trend')}</p>
	</div>
	<div class="chart" bind:this={chart} />
</div>
<div id="tooltip" bind:this={tooltip}>
	<div class="tooltip-box">
		<div class="date">{parseDate}</div>
		<div class="sum">{sum}{$t('global.KRW')}</div>
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	#tooltip {
		position: fixed;
		z-index: 9;
		display: none;
		min-width: 200px;
		max-width: calc(100vw - 80px);
		.tooltip-box {
			display: flex;
			flex-direction: column;
			border-radius: 5px;
			background-color: rgba($color: #46474b, $alpha: 0.7);
			padding: 20px;
			gap: 10px;

			.date {
				white-space: nowrap;
				color: white;
				font-weight: 550;
				font-size: 1.25rem;
				text-align: center;
			}

			.sum {
				max-width: 300px;
				color: white;
				font-size: 1.25rem;
				text-align: center;
			}
		}
	}

	.container {
		display: flex;
		flex-direction: column;
		gap: 10px;
		box-sizing: border-box;
		width: 100%;
		height: 100%;
		padding: 20px 0;

		.chart {
			height: 421px;
			:global(svg .axisGroup text) {
				font-size: 0.875rem;
				fill: #6d6f76;
				font-weight: 200;
			}
			:global(svg path#salesIncome) {
				stroke: #6591fa;
				animation: dash 1s linear forwards;
			}
			:global(svg circle[dataType="salesIncome"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #6591fa;
			}
			:global(svg circle[dataType="salesIncome"].show) {
				stroke: #6591fa;
				fill: #6591fa;
				opacity: 1 !important;
			}
			:global(svg circle[dataType="salesIncome"].hide) {
				opacity: 0;
				fill: #6591fa;
			}
			:global(svg line[dataType="salesIncome"]) {
				stroke: #6591fa;
			}
			:global(svg path#area-salesIncome),
			:global(svg path#area-return) {
				opacity: 0;
				animation-delay: 1s;
				animation-duration: 1s;
				animation-name: showup;
				animation-fill-mode: forwards;
			}
		}

		.header {
			display: flex;
			align-items: center;
			width: 100%;
			gap: 10px;
			padding: 0px 10px;
			box-sizing: border-box;
			.icon {
				width: 24px;
				height: 24px;
			}
			p {
				font-weight: 550;
				font-size: 1.25rem;
				color: #46474b;
			}
		}
	}
	@include DesktopSize {
		.container {
			.header {
				padding: 0px 30px;
			}
			.chart {
				height: 527px;
			}
		}
	}

	@keyframes dash {
		to {
			stroke-dashoffset: 0;
		}
	}
	@keyframes showup {
		to {
			opacity: 1;
		}
	}

</style>
